import { monthLabelFromIndex } from "../../../dashboard-framework/aggregateRows";
import { formatEuros, formatPercent } from "../../../dashboard-framework/format";
import { buildPivot, PivotCellSx, PivotNode, PivotSummaryColumn, TOTAL_COLUMN_KEY } from "../../../dashboard-framework/pivot";
import { typicalMonthValue, TYPICAL_MONTH_COLUMN_KEY, TYPICAL_MONTH_COLUMN_LABEL } from "./financeMonthlyDeviation";

const euros = formatEuros;
const round2 = (value: number) => Math.round(value * 100) / 100;

// Categorias excluídas dos totais "resultado" ("Resumo por empresa" e "Receitas, despesas e
// resultado acumulado"): transferências entre contas próprias e pagamentos assumidos por parceiros.
export const EXCLUDED_NET_CATEGORY_NAMES = ["Transfer Between Accounts", "Paid by partner"];

type MonthlySumRow = {
    sum: { amount: string | number | null } | null;
} & Record<string, string | number | null | undefined | { amount: string | number | null } | null>;

export const monthlySumsFromGroupedRows = (rows: Array<MonthlySumRow>, dateField: string) => {
    const sumsByMonthIndex = new Map<number, number>();
    rows.forEach((row) => {
        const year = Number(row[`${dateField}_year`]);
        const month = Number(row[`${dateField}_month`]);
        if (!year || !month) return;
        const monthIndex = year * 12 + (month - 1);
        const amount = Number(row.sum?.amount || 0);
        sumsByMonthIndex.set(monthIndex, (sumsByMonthIndex.get(monthIndex) || 0) + amount);
    });
    return sumsByMonthIndex;
};

// Gráfico "Receitas, despesas e resultado acumulado": por mês, Receitas (soma das transações
// positivas) e Despesas (magnitude da soma das negativas) em barras, mais o Resultado ACUMULADO no
// período (linha, origem 0 no primeiro mês visível). Recebe os mapas mês→soma positiva e mês→soma
// negativa (ambos já filtrados ao período), e devolve um ponto por mês com receitas/despesas/resultado.
export const revenueCostRunningPoints = (
    revenueByMonth: Map<number, number>,
    costByMonth: Map<number, number>,
    firstMonthIndex: number,
    lastMonthIndex: number
) => {
    let runningTotal = 0;
    const points = [];
    for (let monthIndex = firstMonthIndex; monthIndex <= lastMonthIndex; monthIndex++) {
        const revenue = revenueByMonth.get(monthIndex) || 0;
        const cost = costByMonth.get(monthIndex) || 0;
        runningTotal += revenue + cost;
        points.push({
            monthLabel: monthLabelFromIndex(monthIndex),
            receitas: round2(revenue),
            despesas: round2(Math.abs(cost)),
            resultado: round2(runningTotal),
        });
    }
    return points;
};

export const CENTER_LABELS: Record<string, string> = {
    clinic_porto: "Clínica Porto",
    clinic_braga: "Clínica Braga",
    global: "Global",
};

// Pivot "Custos por centro": valor alocado (transação × percentagem do centro),
// por Centro → Despesa/Receita → categoria mãe → categoria, expansível, × mês (data do documento).
// Cada transação gera uma linha por centro alocado; transações sem centro caem em "-No Value-" com o
// valor total. Despesa = valor < 0, Receita = valor > 0 (o campo `type` é inacessível à app).
type CenterTransaction = {
    id: number | string | null;
    amount: string | number | null;
    description: string | null;
    document_date: string | null;
    category: { name: string | null; parent_transaction_category: { name: string | null } | null } | null;
    vendor: { name: string | null } | null;
    operational_centers: Array<{ name: string | null; percentage: number | null }> | null;
};

// Cada item é uma FATIA de alocação (transação × centro). Os campos extra (id, descrição, data, %, valor
// da transação) alimentam o drill-down por célula, onde a soma das fatias iguala o valor da célula.
export type CenterAllocationItem2 = {
    id: number | string | null;
    center: string;
    type: string;
    parentName: string;
    categoryName: string;
    vendorName: string;
    monthIndex: number;
    allocated: number;
    description: string | null;
    documentDate: string | null;
    percentage: number | null;
    transactionAmount: number;
};

export const centerPivotItems = (transactions: Array<CenterTransaction>) => {
    const items: Array<CenterAllocationItem2> = [];
    transactions.forEach((transaction) => {
        const documentDate = transaction.document_date;
        if (!documentDate) return;
        const year = Number(documentDate.slice(0, 4));
        const month = Number(documentDate.slice(5, 7));
        if (!year || !month) return;
        const monthIndex = year * 12 + (month - 1);
        const amount = Number(transaction.amount || 0);
        const type = amount < 0 ? "Despesa" : "Receita";
        const parentName = transaction.category?.parent_transaction_category?.name || "Sem categoria mãe";
        const categoryName = transaction.category?.name || "Sem categoria";
        const vendorName = transaction.vendor?.name || "Sem fornecedor/cliente";
        const shared = {
            id: transaction.id,
            type,
            parentName,
            categoryName,
            vendorName,
            monthIndex,
            description: transaction.description,
            documentDate,
            transactionAmount: amount,
        };
        const allocations = transaction.operational_centers || [];
        if (allocations.length === 0) {
            items.push({ ...shared, center: "-No Value-", allocated: amount, percentage: null });
            return;
        }
        allocations.forEach((allocation) => {
            const center = CENTER_LABELS[allocation.name || ""] || allocation.name || "Sem centro";
            const percentage = Number(allocation.percentage || 0);
            const allocated = (amount * percentage) / 100;
            items.push({ ...shared, center, allocated, percentage });
        });
    });
    return items;
};

// Com a coluna "Mês típico" a tabela ficou mais estreita por coluna: sem isto valores como
// "-9.029,06 €" partem a meio. Vive no cellSx (e não no estilo partilhado da tabela) para não
// alterar os pivots dos outros dashboards.
const NOWRAP_CELL_SX = { whiteSpace: "nowrap" };

// Semáforo de rentabilidade partilhado: verde se > 0 €, vermelho se < 0 €.
const profitBackgroundSx = (value: number, bold: boolean) => {
    const isPositive = value > 0;
    const sx = {
        backgroundColor: isPositive ? "rgba(76, 175, 80, 0.16)" : "rgba(226, 85, 85, 0.14)",
        color: isPositive ? "#1b5e20" : "#b3261e",
        fontWeight: bold ? 700 : 600,
        ...NOWRAP_CELL_SX,
    };
    return sx;
};

// "Custos por centro": colore a linha Total e as linhas de topo das clínicas (Porto/Braga) por mês.
// Global e "-No Value-" ficam neutros (custos administrativos não atribuídos, sempre negativos —
// colori-los seria ruído). Linhas expandidas (Despesa/Receita/categorias) também neutras.
const CLINIC_CENTER_LABELS = ["Clínica Porto", "Clínica Braga"];
const centerCellSx: PivotCellSx = (rawValue, node, isTotalsRow) => {
    if (rawValue === null) return NOWRAP_CELL_SX;
    const value = Number(rawValue);
    if (value === 0) return NOWRAP_CELL_SX;
    const isClinicRow = !isTotalsRow && node !== undefined && node.depth === 0 && CLINIC_CENTER_LABELS.includes(node.label);
    if (!isTotalsRow && !isClinicRow) return NOWRAP_CELL_SX;
    const sx = profitBackgroundSx(value, Boolean(isTotalsRow));
    return sx;
};

// "Custos e receitas globais": colore SÓ a linha Total — as
// linhas Despesa/Receita/categoria ficam neutras.
const totalOnlyCellSx: PivotCellSx = (rawValue, _node, isTotalsRow) => {
    if (!isTotalsRow || rawValue === null) return NOWRAP_CELL_SX;
    const value = Number(rawValue);
    if (value === 0) return NOWRAP_CELL_SX;
    const sx = profitBackgroundSx(value, true);
    return sx;
};

// "Resumo por empresa": colore a linha Total e as linhas de topo (empresa) por mês, para ler o
// resultado líquido de cada empresa rapidamente. Tipo/categoria/fornecedor ficam neutros.
const companyCellSx: PivotCellSx = (rawValue, node, isTotalsRow) => {
    if (rawValue === null) return NOWRAP_CELL_SX;
    const value = Number(rawValue);
    if (value === 0) return NOWRAP_CELL_SX;
    const isCompanyRow = !isTotalsRow && node !== undefined && node.depth === 0;
    if (!isTotalsRow && !isCompanyRow) return NOWRAP_CELL_SX;
    const sx = profitBackgroundSx(value, Boolean(isTotalsRow));
    return sx;
};

// Chave da coluna de um mês. Fonte única: o dashboard precisa da MESMA chave para dizer aos pivots
// qual é o mês corrente, e uma chave com outro formato falharia em silêncio (o mês corrente entraria
// na mediana e levaria seta).
export const monthColumnKey = (monthIndex: number) => {
    const columnKey = String(monthIndex).padStart(6, "0");
    return columnKey;
};

// O mês corrente é sempre parcial: vai marcado no cabeçalho, fica fora da mediana e nunca leva seta.
const monthColumnLabel = (columnKey: string, currentMonthColumnKey: string | null) => {
    const monthLabel = monthLabelFromIndex(Number(columnKey));
    const label = columnKey === currentMonthColumnKey ? `${monthLabel} (parcial)` : monthLabel;
    return label;
};

const typicalMonthColumn = (currentMonthColumnKey: string | null, cellSx: PivotCellSx): PivotSummaryColumn => {
    const column = {
        key: TYPICAL_MONTH_COLUMN_KEY,
        label: TYPICAL_MONTH_COLUMN_LABEL,
        compute: (nodeValues: { columnKeys: Array<string>; rawByColumn: Record<string, number | null> }) => typicalMonthValue(nodeValues, currentMonthColumnKey),
        format: (value: number | null) => (value === null ? "" : euros(value)),
        cellSx,
    };
    return column;
};

export const centerAllocationPivot = (items: Array<CenterAllocationItem2>, currentMonthColumnKey: string | null = null) => {
    const sumAllocated = (cellItems: Array<CenterAllocationItem2>) => cellItems.reduce((sum, item) => sum + item.allocated, 0);
    const total = (node: PivotNode<CenterAllocationItem2>) => Number(node.rawByColumn[TOTAL_COLUMN_KEY] || 0);
    const byTotalDescending = (first: PivotNode<CenterAllocationItem2>, second: PivotNode<CenterAllocationItem2>) => total(second) - total(first);
    const byTotalAscending = (first: PivotNode<CenterAllocationItem2>, second: PivotNode<CenterAllocationItem2>) => total(first) - total(second);
    const pivot = buildPivot({
        items,
        rowLevels: [(item) => item.center, (item) => item.type, (item) => item.parentName, (item) => item.categoryName, (item) => item.vendorName],
        measures: [{ key: "allocated", label: "Valor", compute: (cellItems) => sumAllocated(cellItems), format: (value) => euros(value || 0), cellSx: centerCellSx }],
        columnKeyOf: (item) => monthColumnKey(item.monthIndex),
        columnLabelOf: (columnKey) => monthColumnLabel(columnKey, currentMonthColumnKey),
        summaryColumn: typicalMonthColumn(currentMonthColumnKey, centerCellSx),
        sortRowsBy: [byTotalDescending, byTotalAscending, byTotalAscending, byTotalAscending, byTotalAscending],
    });
    return pivot;
};

// Pivot "Custos e receitas globais": valor ALOCADO da
// MorningAbility por Tipo (Despesa/Receita) → categoria mãe → categoria, × mês (data do documento).
// Mesma fonte de dados que "Custos por centro" (cada transação repartida por centro; sem centro →
// "-No Value-"), mas sem o nível de centro — é a visão global de custos e receitas por categoria.
// Só a linha Total é colorida (verde/vermelho).
export const categoryRevenueCostPivot = (items: Array<CenterAllocationItem2>, currentMonthColumnKey: string | null = null) => {
    const sumAllocated = (cellItems: Array<CenterAllocationItem2>) => cellItems.reduce((sum, item) => sum + item.allocated, 0);
    const total = (node: PivotNode<CenterAllocationItem2>) => Number(node.rawByColumn[TOTAL_COLUMN_KEY] || 0);
    const byTotalAscending = (first: PivotNode<CenterAllocationItem2>, second: PivotNode<CenterAllocationItem2>) => total(first) - total(second);
    const pivot = buildPivot({
        items,
        rowLevels: [(item) => item.type, (item) => item.parentName, (item) => item.categoryName, (item) => item.vendorName],
        measures: [{ key: "allocated", label: "Valor", compute: (cellItems) => sumAllocated(cellItems), format: (value) => euros(value || 0), cellSx: totalOnlyCellSx }],
        columnKeyOf: (item) => monthColumnKey(item.monthIndex),
        columnLabelOf: (columnKey) => monthColumnLabel(columnKey, currentMonthColumnKey),
        summaryColumn: typicalMonthColumn(currentMonthColumnKey, totalOnlyCellSx),
        sortRowsBy: [byTotalAscending, byTotalAscending, byTotalAscending, byTotalAscending],
    });
    return pivot;
};

// Um empréstimo recebido pela empresa ("Creditos" positivo) é dívida, não receita da clínica — contá-lo
// esconderia a receita real. As prestações ("Creditos" negativos) continuam nas despesas.
const LOAN_CATEGORY_NAME = "Creditos";

export const withoutLoanDisbursements = (items: Array<CenterAllocationItem2>) => {
    const filteredItems = items.filter((item) => !(item.categoryName === LOAN_CATEGORY_NAME && item.type === "Receita"));
    return filteredItems;
};

// "Resumo por empresa" expansível: performance de cada empresa a partir dos valores PUROS das
// transações bancárias (sem alocação a centros). Cada transação vira um item Empresa → Tipo
// (Receita/Despesa, pelo sinal) → categoria mãe → categoria → fornecedor/cliente, com o valor com
// sinal e o mês (data da transação). Os campos extra (id, descrição, data) alimentam o drill de célula.
type CompanyTransaction = {
    id: number | string | null;
    amount: string | number | null;
    description: string | null;
    transaction_date: string | null;
    company: { name: string | null } | null;
    category: { name: string | null; parent_transaction_category: { name: string | null } | null } | null;
    vendor: { name: string | null } | null;
};

export type CompanyPerformanceItem = {
    id: number | string | null;
    company: string;
    type: string;
    parentName: string;
    categoryName: string;
    vendorName: string;
    monthIndex: number;
    amount: number;
    description: string | null;
    transactionDate: string | null;
};

export const companyPivotItems = (transactions: Array<CompanyTransaction>) => {
    const items: Array<CompanyPerformanceItem> = [];
    transactions.forEach((transaction) => {
        const transactionDate = transaction.transaction_date;
        if (!transactionDate) return;
        const year = Number(transactionDate.slice(0, 4));
        const month = Number(transactionDate.slice(5, 7));
        if (!year || !month) return;
        const monthIndex = year * 12 + (month - 1);
        const amount = Number(transaction.amount || 0);
        const type = amount < 0 ? "Despesa" : "Receita";
        const company = transaction.company?.name || "Sem empresa";
        const parentName = transaction.category?.parent_transaction_category?.name || "Sem categoria mãe";
        const categoryName = transaction.category?.name || "Sem categoria";
        const vendorName = transaction.vendor?.name || "Sem fornecedor/cliente";
        items.push({ id: transaction.id, company, type, parentName, categoryName, vendorName, monthIndex, amount, description: transaction.description, transactionDate });
    });
    return items;
};

// Pivot Empresa → Tipo → categoria mãe → categoria → fornecedor/cliente, × mês (data da transação).
// Empresa ordenada pelo resultado (melhor primeiro); Tipo por total ascendente (Despesa primeiro,
// Receita a seguir); restantes níveis por total ascendente (maiores custos no topo).
// Só a linha da empresa e o Total são coloridos (verde/vermelho); os restantes níveis ficam neutros.
export const companyPerformancePivot = (items: Array<CompanyPerformanceItem>, currentMonthColumnKey: string | null = null) => {
    const sumAmount = (cellItems: Array<CompanyPerformanceItem>) => cellItems.reduce((sum, item) => sum + item.amount, 0);
    const total = (node: PivotNode<CompanyPerformanceItem>) => Number(node.rawByColumn[TOTAL_COLUMN_KEY] || 0);
    const byTotalDescending = (first: PivotNode<CompanyPerformanceItem>, second: PivotNode<CompanyPerformanceItem>) => total(second) - total(first);
    const byTotalAscending = (first: PivotNode<CompanyPerformanceItem>, second: PivotNode<CompanyPerformanceItem>) => total(first) - total(second);
    const pivot = buildPivot({
        items,
        rowLevels: [(item) => item.company, (item) => item.type, (item) => item.parentName, (item) => item.categoryName, (item) => item.vendorName],
        measures: [{ key: "amount", label: "Valor", compute: (cellItems) => sumAmount(cellItems), format: (value) => euros(value || 0), cellSx: companyCellSx }],
        columnKeyOf: (item) => monthColumnKey(item.monthIndex),
        columnLabelOf: (columnKey) => monthColumnLabel(columnKey, currentMonthColumnKey),
        summaryColumn: typicalMonthColumn(currentMonthColumnKey, companyCellSx),
        sortRowsBy: [byTotalDescending, byTotalAscending, byTotalAscending, byTotalAscending, byTotalAscending],
    });
    return pivot;
};

// Pivot "Encomendas sem transação bancária" (expansível): pagamentos de encomendas SEM
// transação bancária associada, por Mês (data do pagamento) → encomenda. O fetch traz as linhas
// (connected_bank_transaction_id nulo, método mapeável, 2025/2026); cada encomenda liga a /orders/{id}.
type UnmappedOrderPayment = {
    id: number | string | null;
    payment_date: string | null;
    amount: string | number | null;
    order: number | null;
};

export type UnmappedOrderItem = {
    monthIndex: number;
    orderLabel: string;
    orderId: number | null;
    amount: number;
};

export const unmappedOrdersPivotItems = (payments: Array<UnmappedOrderPayment>) => {
    const items: Array<UnmappedOrderItem> = [];
    payments.forEach((payment) => {
        const paymentDate = payment.payment_date;
        if (!paymentDate) return;
        const year = Number(paymentDate.slice(0, 4));
        const month = Number(paymentDate.slice(5, 7));
        if (!year || !month) return;
        const monthIndex = year * 12 + (month - 1);
        const orderId = payment.order === null || payment.order === undefined ? null : Number(payment.order);
        const orderLabel = orderId === null ? "Sem encomenda" : `Encomenda ${orderId}`;
        items.push({ monthIndex, orderLabel, orderId, amount: Number(payment.amount || 0) });
    });
    return items;
};

export const unmappedOrdersPivot = (items: Array<UnmappedOrderItem>) => {
    const sumAmount = (cellItems: Array<UnmappedOrderItem>) => cellItems.reduce((sum, item) => sum + item.amount, 0);
    const monthOf = (node: PivotNode<UnmappedOrderItem>) => (node.items[0] ? node.items[0].monthIndex : 0);
    const valorOf = (node: PivotNode<UnmappedOrderItem>) => Number(node.rawByColumn.valor || 0);
    const byMonthAscending = (first: PivotNode<UnmappedOrderItem>, second: PivotNode<UnmappedOrderItem>) => monthOf(first) - monthOf(second);
    const byValorDescending = (first: PivotNode<UnmappedOrderItem>, second: PivotNode<UnmappedOrderItem>) => valorOf(second) - valorOf(first);
    const pivot = buildPivot({
        items,
        rowLevels: [(item) => monthLabelFromIndex(item.monthIndex), (item) => item.orderLabel],
        measures: [{ key: "valor", label: "Valor", compute: (cellItems) => round2(sumAmount(cellItems)), format: (value) => euros(value || 0) }],
        sortRowsBy: [byMonthAscending, byValorDescending],
    });
    return pivot;
};

type MethodMonthSumRow = {
    sum: { amount: string | number | null } | null;
} & Record<string, unknown>;

type BankVsOrdersMethod = { label: string; paymentMethod: string; categoryId: number };

export type BankVsOrdersItem = {
    method: string;
    monthIndex: number;
    orders: number;
    bank: number;
};

// Constrói os itens do pivot "Bank vs Orders" a partir de dois agregados mensais: encomendas por
// (método × mês de pagamento) e banco por (categoria × mês de documento). Cada linha vira um item
// {método, mês, orders/bank}; a fusão acontece no buildPivot (mesmo nó método/mês soma ambos).
export const bankVsOrdersPivotItems = (
    ordersRows: Array<MethodMonthSumRow>,
    bankRows: Array<MethodMonthSumRow>,
    methods: Array<BankVsOrdersMethod>
) => {
    const items: Array<BankVsOrdersItem> = [];
    ordersRows.forEach((row) => {
        const method = methods.find((candidate) => candidate.paymentMethod === row.payment_method);
        if (!method) return;
        const year = Number(row.payment_date_year);
        const month = Number(row.payment_date_month);
        if (!year || !month) return;
        items.push({ method: method.label, monthIndex: year * 12 + (month - 1), orders: Number(row.sum?.amount || 0), bank: 0 });
    });
    bankRows.forEach((row) => {
        const method = methods.find((candidate) => candidate.categoryId === Number(row.category));
        if (!method) return;
        const year = Number(row.document_date_year);
        const month = Number(row.document_date_month);
        if (!year || !month) return;
        items.push({ method: method.label, monthIndex: year * 12 + (month - 1), orders: 0, bank: Number(row.sum?.amount || 0) });
    });
    return items;
};

// Pivot Método → Mês, colunas Encomendas / Banco / Dif. / % (Dif = Banco − Encomendas;
// % = (Encomendas − Banco) / Encomendas). Métodos na ordem definida; meses cronológicos.
export const bankVsOrdersPivot = (items: Array<BankVsOrdersItem>, methods: Array<BankVsOrdersMethod>) => {
    const sumOrders = (cellItems: Array<BankVsOrdersItem>) => cellItems.reduce((sum, item) => sum + item.orders, 0);
    const sumBank = (cellItems: Array<BankVsOrdersItem>) => cellItems.reduce((sum, item) => sum + item.bank, 0);
    const monthOf = (node: PivotNode<BankVsOrdersItem>) => (node.items[0] ? node.items[0].monthIndex : 0);
    const methodOrder = (node: PivotNode<BankVsOrdersItem>) => methods.findIndex((method) => method.label === node.label);
    const byMethodOrder = (first: PivotNode<BankVsOrdersItem>, second: PivotNode<BankVsOrdersItem>) => methodOrder(first) - methodOrder(second);
    const byMonthAscending = (first: PivotNode<BankVsOrdersItem>, second: PivotNode<BankVsOrdersItem>) => monthOf(first) - monthOf(second);
    const pivot = buildPivot({
        items,
        rowLevels: [(item) => item.method, (item) => monthLabelFromIndex(item.monthIndex)],
        measures: [
            { key: "orders", label: "Encomendas", compute: (cellItems) => round2(sumOrders(cellItems)), format: (value) => euros(value || 0) },
            { key: "bank", label: "Banco", compute: (cellItems) => round2(sumBank(cellItems)), format: (value) => euros(value || 0) },
            { key: "dif", label: "Dif.", compute: (cellItems) => round2(sumBank(cellItems) - sumOrders(cellItems)), format: (value) => euros(value || 0) },
            {
                key: "pct",
                label: "%",
                compute: (cellItems) => {
                    const orders = sumOrders(cellItems);
                    if (orders === 0) return null;
                    const value = ((orders - sumBank(cellItems)) / orders) * 100;
                    return value;
                },
                format: (value) => (value === null ? "—" : formatPercent(value, 1)),
            },
        ],
        sortRowsBy: [byMethodOrder, byMonthAscending],
    });
    return pivot;
};

type SingleSumRow = { sum: { amount: string | number | null } | null };

export const singleSumAmount = (rows: Array<SingleSumRow> | undefined) => {
    const total = Number(rows?.[0]?.sum?.amount || 0);
    return total;
};

// "Receita de Hoje": total dos pagamentos de encomendas de hoje + repartição por clínica (clínica da
// encomenda, `order.clinic.name`). Recebe as linhas do dia e devolve o total e as clínicas por valor desc.
type TodayPaymentRow = {
    amount: string | number | null;
    order: { clinic: { name: string | null } | null } | null;
};

export const todayRevenueByClinic = (payments: Array<TodayPaymentRow>) => {
    let total = 0;
    const amountByClinic = new Map<string, number>();
    payments.forEach((payment) => {
        const amount = Number(payment.amount || 0);
        total += amount;
        const clinic = payment.order?.clinic?.name || "Sem clínica";
        amountByClinic.set(clinic, (amountByClinic.get(clinic) || 0) + amount);
    });
    const clinics = Array.from(amountByClinic.entries())
        .map(([clinic, amount]) => ({ clinic, amount: round2(amount) }))
        .sort((first, second) => second.amount - first.amount);
    const result = { total: round2(total), clinics };
    return result;
};

type BankBalanceRow = {
    balance: string | number | null;
    company: number | null;
};

export const bankBalanceTotals = (banks: Array<BankBalanceRow>, neuroImproveCompanyId: number) => {
    let allAccounts = 0;
    let neuroImproveAccounts = 0;
    banks.forEach((bank) => {
        const balance = Number(bank.balance || 0);
        allAccounts += balance;
        if (bank.company === neuroImproveCompanyId) neuroImproveAccounts += balance;
    });
    const totals = { allAccounts: round2(allAccounts), neuroImproveAccounts: round2(neuroImproveAccounts) };
    return totals;
};

type RevenueProjectionInput = {
    paymentsTotal: number;
    businessDaysElapsed: number;
    businessDaysInMonth: number;
};

const PESSIMISTIC_PROJECTION_FACTOR = 0.9;

export const monthlyRevenueProjection = ({ paymentsTotal, businessDaysElapsed, businessDaysInMonth }: RevenueProjectionInput) => {
    if (businessDaysElapsed === 0) return null;
    const dailyAverage = paymentsTotal / businessDaysElapsed;
    const optimistic = round2(dailyAverage * businessDaysInMonth);
    const projection = { dailyAverage: round2(dailyAverage), optimistic, pessimistic: round2(optimistic * PESSIMISTIC_PROJECTION_FACTOR) };
    return projection;
};

